import React, { useEffect, useMemo, useRef, useState } from "react";
import { useUnsavedChangesGuard } from "../../hooks/useUnsavedChanges";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { InputSwitch } from "primereact/inputswitch";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { FormField, FormRow, FormSection, FormSections } from "../ui";
import { Message } from "primereact/message";

import {
  useGetBenefitPartnersQuery,
  useCreateBenefitPartnerMutation,
  useUpdateBenefitPartnerMutation,
  useGetBenefitCategoriesQuery,
} from "../../store/services/benefits";
import { useTenantScope } from "../../hooks/useTenantScope";
import { FormActions, useTenantGate } from "../ui";
import { uploadImageToS3 } from "../../utils/functions/uploadToS3";
import { yupConstants } from "../../utils/constants/yup";
import {
  BENEFIT_SLUG_REGEX,
  BENEFIT_WHATSAPP_FORMAT_MESSAGE,
  benefitStatusOptions,
  isValidBenefitWhatsappNumber,
  normalizeBenefitWhatsappNumber,
  sanitizeBenefitWhatsappNumberInput,
} from "../../utils/constants/benefits-constants";
import { BenefitPartner, BenefitPartnerInput, BenefitStatus } from "../../models/benefits";
import {
  getAdminFacingApiErrorMessage,
  getApiErrorStatus,
  isObjectRecord,
} from "../../utils/functions/api-errors";
import { BenefitLocationsManager } from "./BenefitLocationsManager";

const SLUG_TAKEN_MESSAGE = "Ya existe un partner con ese slug";
const SLUG_HELPER_TEXT = "Minúsculas, números y guiones";
const CATEGORY_REQUIRED_MESSAGE =
  "Seleccioná una categoría para guardar el partner";

const ALLOWED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp"];
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

const slugify = (value: string): string =>
  value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const validateImageFile = (file: File): string | null => {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return "Formato de imagen no soportado. Usá PNG, JPG o WebP.";
  }
  if (file.size > MAX_IMAGE_BYTES) {
    return "La imagen supera el máximo de 5 MB.";
  }
  return null;
};

const getApiErrorCode = (error: unknown): string | undefined => {
  if (!isObjectRecord(error)) return undefined;
  const data = error.data;
  if (!isObjectRecord(data)) return undefined;
  if (typeof data.code === "string") return data.code;
  const nested = data.error;
  if (isObjectRecord(nested) && typeof nested.code === "string") {
    return nested.code;
  }
  return undefined;
};

interface BenefitPartnerFormValues {
  name: string;
  slug: string;
  description: string;
  categoryId: string;
  whatsappNumber: string;
  instagramHandle: string;
  contactEmail: string;
  featured: boolean;
  status: BenefitStatus;
}

interface BenefitPartnerFormProps {
  partnerData?: BenefitPartner;
}

export const BenefitPartnerForm = ({ partnerData }: BenefitPartnerFormProps) => {
  const navigate = useNavigate();
  const isEditMode = !!partnerData;

  const tenantScope = useTenantScope();

  // MT-011 — Tenant sobre el que opera la pantalla:
  // - EDICIÓN: el tenant propio del partner (no se puede mover de tenant).
  // - ALTA: el elegido en el dropdown "Tenant" del form, o el del scope
  //   (TENANT_ADMIN / SUPER_ADMIN con tenant fijado en la barra superior).
  // Manda el header X-Tenant-Context de todos los requests de la pantalla, así
  // las categorías y el chequeo de slug son los del tenant correcto.
  // §6.4 / INC-05 — el tenant se hereda del selector del header; el formulario
  // ya no lo elige. En alta, si el header está en "Todos" no hay tenant al que
  // asignar el partner y `tenantGate` bloquea la creación.
  const tenantGate = useTenantGate();
  const effectiveTenantId = isEditMode
    ? partnerData?.tenantId ?? null
    : tenantScope.tenantId;

  const { data: categories, isLoading: isLoadingCategories } =
    useGetBenefitCategoriesQuery(
      { tenantId: effectiveTenantId },
      { skip: !effectiveTenantId }
    );

  // Para feedback inmediato de slug duplicado dentro del tenant destino.
  const { data: allPartners } = useGetBenefitPartnersQuery({
    tenantId: effectiveTenantId,
  });

  const [createPartner, { isLoading: isCreating }] =
    useCreateBenefitPartnerMutation();
  const [updatePartner, { isLoading: isUpdating }] =
    useUpdateBenefitPartnerMutation();

  const isSaving = isCreating || isUpdating;

  // Archivos pendientes (la API recibe multipart inline, sin presigned URLs).
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  // Quitar el logo / la portada ya guardados (en edición): viajan como null al
  // guardar para que el backend los borre. Antes solo se podían reemplazar.
  const [removeSavedLogo, setRemoveSavedLogo] = useState(false);
  const [removeSavedCover, setRemoveSavedCover] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);

  const logoPreviewUrl = useMemo(
    () => (logoFile ? URL.createObjectURL(logoFile) : null),
    [logoFile]
  );
  const coverPreviewUrl = useMemo(
    () => (coverFile ? URL.createObjectURL(coverFile) : null),
    [coverFile]
  );

  useEffect(() => {
    return () => {
      if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
    };
  }, [logoPreviewUrl]);

  useEffect(() => {
    return () => {
      if (coverPreviewUrl) URL.revokeObjectURL(coverPreviewUrl);
    };
  }, [coverPreviewUrl]);

  // Auto-generación de slug (solo en alta y mientras el usuario no lo editó).
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  const goBack = () => navigate(-1);

  const {
    handleSubmit,
    validateForm,
    setTouched,
    touched,
    errors,
    values,
    handleBlur,
    handleChange,
    setFieldValue,
    setFieldTouched,
    dirty,
  } = useFormik<BenefitPartnerFormValues>({
    initialValues: {
      name: partnerData?.name || "",
      slug: partnerData?.slug || "",
      description: partnerData?.description || "",
      categoryId: partnerData?.categoryId || "",
      whatsappNumber: partnerData?.whatsappNumber || "",
      instagramHandle: (partnerData?.instagramHandle || "").replace(/^@+/, ""),
      contactEmail: partnerData?.contactEmail || "",
      featured: partnerData?.featured ?? false,
      status: partnerData?.status || "active",
    },
    enableReinitialize: isEditMode,
    validationSchema: Yup.object({
      name: Yup.string().trim().required(yupConstants().required),
      slug: Yup.string()
        .trim()
        .required(yupConstants().required)
        .matches(BENEFIT_SLUG_REGEX, SLUG_HELPER_TEXT),
      categoryId: Yup.string().trim().required(CATEGORY_REQUIRED_MESSAGE),
      whatsappNumber: Yup.string()
        .trim()
        .test(
          "whatsapp-format",
          BENEFIT_WHATSAPP_FORMAT_MESSAGE,
          (value) => isValidBenefitWhatsappNumber(value ?? "")
        ),
      contactEmail: Yup.string().email(yupConstants().email),
      status: Yup.string().required(yupConstants().required),
    }),
    validate: (vals) => {
      const validationErrors: Partial<Record<keyof BenefitPartnerFormValues, string>> = {};
      const slug = vals.slug.trim();
      if (slug && Array.isArray(allPartners)) {
        const isTaken = allPartners.some(
          (partner) =>
            partner.slug === slug &&
            partner.id !== partnerData?.id &&
            (!effectiveTenantId || partner.tenantId === effectiveTenantId)
        );
        if (isTaken) validationErrors.slug = SLUG_TAKEN_MESSAGE;
      }
      return validationErrors;
    },
    onSubmit: async (vals, { setFieldError }) => {
      // Las imágenes se suben desde el navegador via presigned URL (mismo flujo que
      // eventos): al backend viajan solo las URLs públicas resultantes.
      let logoUrl: string | undefined;
      let coverImageUrl: string | undefined;
      try {
        [logoUrl, coverImageUrl] = await Promise.all([
          logoFile
            ? uploadImageToS3(logoFile, "benefits", logoFile.name)
            : Promise.resolve(undefined),
          coverFile
            ? uploadImageToS3(coverFile, "benefits", coverFile.name)
            : Promise.resolve(undefined),
        ]);
      } catch {
        toast.error("No se pudieron subir las imágenes. Probá de nuevo.");
        return;
      }

      const input: BenefitPartnerInput = {
        name: vals.name.trim(),
        slug: vals.slug.trim(),
        description: vals.description,
        categoryId: vals.categoryId.trim(),
        // Vacíos: al crear se omiten; al editar viajan como null para que el
        // backend los borre (si no, conservaría el valor anterior).
        whatsappNumber: vals.whatsappNumber.trim()
          ? normalizeBenefitWhatsappNumber(vals.whatsappNumber)
          : partnerData
            ? null
            : undefined,
        instagramHandle: vals.instagramHandle.trim().replace(/^@+/, ""),
        contactEmail: vals.contactEmail.trim() || (partnerData ? null : undefined),
        featured: vals.featured,
        status: vals.status,
        logoUrl:
          logoUrl ?? (partnerData?.logoUrl && removeSavedLogo ? null : undefined),
        coverImageUrl:
          coverImageUrl ??
          (partnerData?.coverImageUrl && removeSavedCover ? null : undefined),
      };

      try {
        if (partnerData) {
          // El backend ignora tenantId en update: no se envía en el body. El
          // tenant del partner sí viaja como header para operar sobre él.
          await updatePartner({
            id: partnerData.id,
            contextTenantId: partnerData.tenantId,
            ...input,
          }).unwrap();
          setLogoFile(null);
          setCoverFile(null);
          toast.success("Partner actualizado");
          navigate("/benefits-partners");
        } else {
          if (effectiveTenantId) input.tenantId = effectiveTenantId;
          const created = await createPartner({
            ...input,
            contextTenantId: effectiveTenantId,
          }).unwrap();
          toast.success("Partner creado");
          navigate(`/benefits-partners/${created.id}/edit`);
        }
      } catch (err) {
        const status = getApiErrorStatus(err);
        const code = getApiErrorCode(err);
        if (status === 409 || code === "BENEFIT_PARTNER_SLUG_TAKEN") {
          setFieldError("slug", SLUG_TAKEN_MESSAGE);
          toast.error(SLUG_TAKEN_MESSAGE);
        } else if (code === "BENEFIT_PARTNER_CATEGORY_REQUIRED") {
          setFieldError("categoryId", CATEGORY_REQUIRED_MESSAGE);
          toast.error(CATEGORY_REQUIRED_MESSAGE);
        } else {
          toast.error(
            getAdminFacingApiErrorMessage(
              err,
              partnerData
                ? "No se pudo actualizar el partner"
                : "No se pudo crear el partner"
            )
          );
        }
      }
    },
  });

  // Avisa antes de que un cambio de tenant en el header recargue la pantalla
  // y se lleve puesto lo que se estaba cargando.
  useUnsavedChangesGuard(
    dirty || logoFile !== null || coverFile !== null || removeSavedLogo || removeSavedCover
  );

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(e);
    if (!isEditMode && !slugManuallyEdited) {
      setFieldValue("slug", slugify(e.target.value));
    }
  };

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSlugManuallyEdited(true);
    handleChange(e);
  };

  const handleInstagramChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue("instagramHandle", e.target.value.replace(/^@+/, ""));
  };

  const handleWhatsappChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue(
      "whatsappNumber",
      sanitizeBenefitWhatsappNumberInput(e.target.value)
    );
  };

  const handleFileSelect =
    (setFile: React.Dispatch<React.SetStateAction<File | null>>) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file) return;
      const fileError = validateImageFile(file);
      if (fileError) {
        toast.error(fileError);
        return;
      }
      setFile(file);
    };

  // Las categorías ya vienen scopeadas por el header del tenant efectivo; el
  // filtro extra es defensivo (la categoría DEBE ser de ese tenant, si no el
  // backend responde BENEFIT_CATEGORY_NOT_FOUND).
  const categoryOptions = useMemo(
    () =>
      (categories ?? [])
        .filter(
          (category) =>
            !effectiveTenantId || category.tenantId === effectiveTenantId
        )
        .map((category) => ({
          label: category.name || category.id,
          value: category.id,
        })),
    [categories, effectiveTenantId]
  );

  // Si al cambiar el tenant la categoría elegida ya no pertenece a ese tenant,
  // se limpia para no enviar una categoría inválida. Solo con las categorías ya
  // resueltas: mientras cargan no hay que pisar la categoría del partner.
  useEffect(() => {
    if (
      categories &&
      values.categoryId &&
      !categoryOptions.some((option) => option.value === values.categoryId)
    ) {
      setFieldValue("categoryId", "");
    }
  }, [categories, categoryOptions, values.categoryId, setFieldValue]);

  const logoPreviewSrc =
    logoPreviewUrl || (removeSavedLogo ? "" : partnerData?.logoUrl) || "";
  const coverPreviewSrc =
    coverPreviewUrl || (removeSavedCover ? "" : partnerData?.coverImageUrl) || "";

  // Separado del onSubmit del <form> porque los botones de acción viven fuera
  // de él (van después de Ubicaciones, que no puede anidarse en el <form>).
  const runSubmit = async () => {
    const validationErrors = await validateForm();
    await setTouched(
      {
        name: true,
        slug: true,
        description: true,
        categoryId: true,
        whatsappNumber: true,
        instagramHandle: true,
        contactEmail: true,
        featured: true,
        status: true,
      },
      true
    );
    if (validationErrors.categoryId) {
      toast.error(validationErrors.categoryId);
    }
    handleSubmit();
  };

  // Fallback para Enter dentro de un input del form.
  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    runSubmit();
  };

  const partnerErrorText = (field: keyof typeof values): string | undefined =>
    touched[field] && errors[field] ? (errors[field] as string) : undefined;

  const imageStyle = {
    objectFit: "cover" as const,
    borderRadius: "8px",
    border: "1px solid var(--surface-border)",
    display: "block",
  };

  return (
    <>
      <form onSubmit={handleFormSubmit} className="p-fluid">
        <FormSections>
        {/* ========================================= */}
        {/* Información básica                        */}
        {/* ========================================= */}
        {/* §5 — etiqueta arriba, grilla de 3 columnas iguales (campos alineados
            y simétricos). Aclaraciones en la etiqueta o el placeholder, nunca
            texto suelto debajo del campo (solo errores). */}
        <FormSection title="Información básica">
          <FormRow columns={2}>
            <FormField label="Nombre" htmlFor="name" required error={partnerErrorText("name")}>
              <InputText
                id="name"
                name="name"
                placeholder="Ej: Café Central"
                value={values.name}
                onChange={handleNameChange}
                onBlur={handleBlur}
                className={partnerErrorText("name") ? "p-invalid" : ""}
                disabled={isSaving}
              />
            </FormField>
            <FormField label="Slug" htmlFor="slug" required error={partnerErrorText("slug")}>
              <InputText
                id="slug"
                name="slug"
                value={values.slug}
                onChange={handleSlugChange}
                onBlur={handleBlur}
                className={partnerErrorText("slug") ? "p-invalid" : ""}
                disabled={isSaving}
                placeholder="Ej: cafe-central (minúsculas, números y guiones)"
                title={SLUG_HELPER_TEXT}
              />
            </FormField>
          </FormRow>
          <FormRow columns={3}>
            <FormField
              label="Categoría"
              htmlFor="categoryId"
              required
              error={partnerErrorText("categoryId")}
            >
              <Dropdown
                inputId="categoryId"
                value={values.categoryId || null}
                options={categoryOptions}
                optionLabel="label"
                optionValue="value"
                onChange={(e) => setFieldValue("categoryId", e.value ?? "")}
                onBlur={() => setFieldTouched("categoryId", true)}
                loading={isLoadingCategories}
                placeholder={
                  effectiveTenantId
                    ? "Seleccionar categoría"
                    : "Elegí primero el tenant"
                }
                className={partnerErrorText("categoryId") ? "p-invalid" : ""}
                disabled={isSaving || !effectiveTenantId}
                emptyMessage="No hay categorías todavía. Creá una en Beneficios → Categorías."
              />
            </FormField>
            <FormField label="Estado" htmlFor="status" required error={partnerErrorText("status")}>
              <Dropdown
                inputId="status"
                placeholder="Seleccionar estado"
                value={values.status}
                options={benefitStatusOptions}
                optionLabel="label"
                optionValue="value"
                onChange={(e) => setFieldValue("status", e.value)}
                className={partnerErrorText("status") ? "p-invalid" : ""}
                disabled={isSaving}
              />
            </FormField>
            <FormField label="Destacado" htmlFor="featured">
              {/* Misma altura que un input, para que la fila quede pareja. */}
              <div className="flex align-items-center gap-2" style={{ minHeight: "2.75rem" }}>
                <InputSwitch
                  inputId="featured"
                  checked={values.featured}
                  onChange={(e) => setFieldValue("featured", e.value)}
                  disabled={isSaving}
                />
                <span className="text-600">{values.featured ? "Sí" : "No"}</span>
              </div>
            </FormField>
            <FormField label="Descripción" htmlFor="description" span="full">
              <InputTextarea
                id="description"
                name="description"
                placeholder="Contá brevemente qué ofrece el partner"
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
                rows={4}
                autoResize
                disabled={isSaving}
              />
            </FormField>
          </FormRow>
        </FormSection>

        <FormSection title="Contacto">
          <FormRow columns={3}>
            <FormField label="WhatsApp" htmlFor="whatsappNumber" error={partnerErrorText("whatsappNumber")}>
              <InputText
                id="whatsappNumber"
                name="whatsappNumber"
                value={values.whatsappNumber}
                onChange={handleWhatsappChange}
                onBlur={handleBlur}
                inputMode="tel"
                keyfilter={/[\d+\s\-()]/}
                className={partnerErrorText("whatsappNumber") ? "p-invalid" : ""}
                disabled={isSaving}
                placeholder="Ej: +54 11 2345 6789"
              />
            </FormField>
            <FormField label="Instagram" htmlFor="instagramHandle">
              <div className="p-inputgroup">
                <span className="p-inputgroup-addon">@</span>
                <InputText
                  id="instagramHandle"
                  name="instagramHandle"
                  value={values.instagramHandle}
                  onChange={handleInstagramChange}
                  onBlur={handleBlur}
                  disabled={isSaving}
                  placeholder="usuario"
                />
              </div>
            </FormField>
            <FormField label="Email de contacto" htmlFor="contactEmail" error={partnerErrorText("contactEmail")}>
              <InputText
                id="contactEmail"
                name="contactEmail"
                value={values.contactEmail}
                onChange={handleChange}
                onBlur={handleBlur}
                className={partnerErrorText("contactEmail") ? "p-invalid" : ""}
                disabled={isSaving}
                placeholder="contacto@partner.com"
              />
            </FormField>
          </FormRow>
        </FormSection>

        <FormSection title="Imágenes">
          <FormRow columns={2}>
            <FormField label="Logo">
              {logoPreviewSrc && (
                <img
                  src={logoPreviewSrc}
                  alt="Logo del partner"
                  style={{ ...imageStyle, width: "120px", height: "120px" }}
                />
              )}
              <div className="flex flex-wrap align-items-center gap-2">
                <Button
                  type="button"
                  label={logoPreviewSrc ? "Cambiar logo" : "Subir logo"}
                  icon="pi pi-upload"
                  outlined
                  className="w-auto"
                  onClick={() => logoInputRef.current?.click()}
                  disabled={isSaving}
                />
                {logoPreviewSrc && (
                  <Button
                    type="button"
                    label="Quitar"
                    icon="pi pi-times"
                    severity="secondary"
                    outlined
                    className="w-auto"
                    // Primero descarta el nuevo sin guardar; si no hay, quita el guardado.
                    onClick={() => (logoFile ? setLogoFile(null) : setRemoveSavedLogo(true))}
                    disabled={isSaving}
                  />
                )}
                <small className="text-600">
                  {logoFile
                    ? `Pendiente de guardar: ${logoFile.name}`
                    : removeSavedLogo && partnerData?.logoUrl
                      ? "El logo se quita al guardar"
                      : "PNG, JPG o WebP · máx. 5 MB"}
                </small>
              </div>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                style={{ display: "none" }}
                onChange={handleFileSelect(setLogoFile)}
              />
            </FormField>
            <FormField label="Portada">
              {coverPreviewSrc && (
                <img
                  src={coverPreviewSrc}
                  alt="Portada del partner"
                  style={{ ...imageStyle, width: "100%", maxWidth: "360px", aspectRatio: "16 / 9" }}
                />
              )}
              <div className="flex flex-wrap align-items-center gap-2">
                <Button
                  type="button"
                  label={coverPreviewSrc ? "Cambiar portada" : "Subir portada"}
                  icon="pi pi-upload"
                  outlined
                  className="w-auto"
                  onClick={() => coverInputRef.current?.click()}
                  disabled={isSaving}
                />
                {coverPreviewSrc && (
                  <Button
                    type="button"
                    label="Quitar"
                    icon="pi pi-times"
                    severity="secondary"
                    outlined
                    className="w-auto"
                    // Primero descarta la nueva sin guardar; si no hay, quita la guardada.
                    onClick={() => (coverFile ? setCoverFile(null) : setRemoveSavedCover(true))}
                    disabled={isSaving}
                  />
                )}
                <small className="text-600">
                  {coverFile
                    ? `Pendiente de guardar: ${coverFile.name}`
                    : removeSavedCover && partnerData?.coverImageUrl
                      ? "La portada se quita al guardar"
                      : "PNG, JPG o WebP · máx. 5 MB"}
                </small>
              </div>
              <input
                ref={coverInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                style={{ display: "none" }}
                onChange={handleFileSelect(setCoverFile)}
              />
            </FormField>
          </FormRow>
        </FormSection>
        </FormSections>
      </form>

      {/* ========================================= */}
      {/* Ubicaciones (fuera del <form> para evitar */}
      {/* submits anidados)                         */}
      {/* ========================================= */}
      {/* Vive fuera del <form> para no anidar submits, así que no es hermana de
          las otras tres y FormSections no puede numerarla: el 4 va explícito. */}
      {partnerData?.id ? (
        <BenefitLocationsManager
          partnerId={partnerData.id}
          tenantId={partnerData.tenantId}
          sectionNumber={4}
        />
      ) : (
        <FormSection title="Ubicaciones" number={4}>
          <Message
            severity="info"
            text="Guardá el partner para poder cargar ubicaciones"
          />
        </FormSection>
      )}

      {/* ========================================= */}
      {/* Botones de acción (van después de Ubicaciones, ya que esta última  */}
      {/* vive fuera del <form>)                                            */}
      {/* ========================================= */}
      <FormActions
        type="button"
        align="end"
        primaryLabel={isEditMode ? "Guardar cambios" : "Crear Partner"}
        onPrimary={runSubmit}
        loading={isSaving}
        disabled={!isEditMode && !tenantGate.allowed}
        disabledReason={tenantGate.reason}
        onCancel={goBack}
      />
    </>
  );
};
